import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Lock,
  MessageSquare,
  Pencil,
  Plus,
  Send,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  facilitatorNotesAPI,
  forumsAPI,
  growGoalPromptsAPI,
} from '../api/client';
import MicButton from '../components/MicButton';
import { ManageMemberById } from '../components/ManageMemberPanel';
import type { FacilitatorMemberDetail as MemberDetail, FacilitatorNote } from '../types';

const TABS = [
  'Overview',
  'Billing',
  'Snapshots',
  'Grow Goals',
  'Prayers',
  'Facilitator Notes',
] as const;
type Tab = (typeof TABS)[number];
const PASTORAL_TABS = new Set<Tab>([
  'Snapshots',
  'Grow Goals',
  'Prayers',
  'Facilitator Notes',
]);

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const BILLING_STATUS_LABEL: Record<string, string> = {
  active: 'Paid',
  trialing: 'Signed up — bills on billing day',
  pending: 'Awaiting confirmation',
  past_due: 'Past due',
  unpaid: 'Payment not started',
  canceled: 'Canceled',
  failed: 'Payment failed',
};

function lastPaidLine(at: string | null, amount: string | null): string {
  if (!at) return '—';
  return amount ? `$${amount} · ${formatDate(at)}` : formatDate(at);
}

function nextChargeLine(payment: MemberDetail['payment']): string {
  if (payment.is_paused) return 'Paused';
  const amount = Number(payment.monthly_amount);
  if (Number.isFinite(amount) && amount === 0) return '$0 · no charge';
  if (payment.next_charge_date) {
    const date = formatDate(payment.next_charge_date);
    if (payment.cancel_at_period_end && payment.status !== 'canceled') {
      return date;
    }
    return payment.status === 'trialing' ? `First charge ${date}` : date;
  }
  if (!payment.has_subscription) return 'Checkout not finished';
  return '—';
}

export default function FacilitatorMemberDetail() {
  const { forumId: forumParam, memberId: memberParam } = useParams();
  const forumId = Number(forumParam);
  const memberId = Number(memberParam);
  const [detail, setDetail] = useState<MemberDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [busyAction, setBusyAction] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [managing, setManaging] = useState(false);

  useEffect(() => {
    let active = true;
    if (!Number.isInteger(forumId) || !Number.isInteger(memberId)) {
      setError('This member link is invalid.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    forumsAPI
      .getMemberDetail(forumId, memberId)
      .then((data) => {
        if (active) setDetail(data);
      })
      .catch((requestError) => {
        if (!active) return;
        const status = requestError?.response?.status;
        setError(
          status === 403
            ? 'You do not have permission to manage this Forum member.'
            : status === 404
              ? 'This member is not in the selected Forum.'
              : 'Could not load this member. Please try again.',
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [forumId, memberId]);

  const updateNotes = (notes: FacilitatorNote[]) => {
    setDetail((current) => (current ? { ...current, notes } : current));
  };

  const refreshDetail = () => {
    forumsAPI.getMemberDetail(forumId, memberId).then(setDetail).catch(() => {
      /* Keep the current view if a background refresh fails. */
    });
  };

  const addNote = async () => {
    if (!detail || !noteDraft.trim() || busyAction) return;
    setBusyAction('note');
    try {
      const note = await facilitatorNotesAPI.create({
        member: memberId,
        forum: forumId,
        content: noteDraft.trim(),
      });
      updateNotes([note, ...detail.notes]);
      setNoteDraft('');
      toast.success('Private note saved');
    } catch {
      toast.error('Could not save note');
    } finally {
      setBusyAction('');
    }
  };

  const saveNote = async (id: number) => {
    if (!detail || !editDraft.trim()) return;
    try {
      const updated = await facilitatorNotesAPI.update(id, editDraft.trim());
      updateNotes(detail.notes.map((note) => (note.id === id ? updated : note)));
      setEditingNote(null);
      setEditDraft('');
    } catch {
      toast.error('Could not update note');
    }
  };

  const deleteNote = async (id: number) => {
    if (!detail || !window.confirm('Delete this private facilitator note?')) return;
    try {
      await facilitatorNotesAPI.delete(id);
      updateNotes(detail.notes.filter((note) => note.id !== id));
    } catch {
      toast.error('Could not delete note');
    }
  };

  const remindGrowGoal = async () => {
    if (!detail || !window.confirm(`Send ${detail.member.full_name} a grow-goal reminder?`))
      return;
    setBusyAction('reminder');
    try {
      await growGoalPromptsAPI.remindMember(forumId, memberId);
      toast.success('Grow-goal reminder sent');
    } catch {
      toast.error('Could not send the reminder');
    } finally {
      setBusyAction('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[18rem] flex items-center justify-center" aria-live="polite">
        <Loader2 className="animate-spin text-navy" size={32} />
        <span className="sr-only">Loading member details</span>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
        <h1 className="text-xl font-semibold text-navy">Member unavailable</h1>
        <p className="text-gray-600 mt-2">{error}</p>
        <Link
          to={`/forum${Number.isInteger(forumId) ? `?forum=${forumId}` : ''}`}
          className="inline-flex items-center gap-2 mt-5 text-navy font-medium"
        >
          <ArrowLeft size={16} /> Back to Forum
        </Link>
      </div>
    );
  }

  const location = [
    detail.member.location_city,
    detail.member.location_state,
    detail.member.location_country,
  ]
    .filter(Boolean)
    .join(', ');
  const canViewPastoral = detail.permissions.can_view_pastoral;
  if (!canViewPastoral) {
    return (
      <Navigate
        to={`/forum${Number.isInteger(forumId) ? `?forum=${forumId}` : ''}`}
        replace
      />
    );
  }
  const visibleTabs = TABS.filter((tab) => !PASTORAL_TABS.has(tab) || canViewPastoral);
  const currentTab = visibleTabs.includes(activeTab) ? activeTab : 'Overview';

  return (
    <div className="space-y-5">
      <header className="bg-white rounded-2xl p-5 sm:p-7 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-off-white flex items-center justify-center overflow-hidden text-xl font-bold text-navy">
            {detail.member.avatar_url ? (
              <img
                src={detail.member.avatar_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              detail.member.full_name
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gold font-medium">{detail.forum.name}</p>
            <h1 className="text-2xl font-bold text-navy truncate">
              {detail.member.full_name}
            </h1>
            <p className="text-gray-500 capitalize">
              {[detail.member.industry, location, detail.member.role.replace('_', ' ')]
                .filter(Boolean)
                .join(' • ')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setManaging(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-navy hover:bg-gray-50"
            >
              <SlidersHorizontal size={16} /> Manage
            </button>
            <Link
              to={`/messages?recipient=${memberId}`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-navy text-white rounded-lg font-medium"
            >
              <MessageSquare size={16} /> Message
            </Link>
          </div>
        </div>
      </header>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div
          role="tablist"
          aria-label="Member details"
          className="flex overflow-x-auto border-b border-gray-100 px-2 sm:px-4"
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              id={`member-tab-${tab.replaceAll(' ', '-').toLowerCase()}`}
              role="tab"
              aria-selected={currentTab === tab}
              aria-controls="member-tab-panel"
              tabIndex={currentTab === tab ? 0 : -1}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap px-3 sm:px-4 py-4 text-sm font-medium border-b-2 transition ${
                currentTab === tab
                  ? 'border-gold text-navy'
                  : 'border-transparent text-gray-500 hover:text-navy'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div
          id="member-tab-panel"
          role="tabpanel"
          aria-labelledby={`member-tab-${currentTab.replaceAll(' ', '-').toLowerCase()}`}
          className="p-5 sm:p-7 min-h-[18rem]"
        >
          {currentTab === 'Overview' && (
            <div className="grid md:grid-cols-2 gap-5">
              <InfoCard title="Profile">
                {detail.member.bio && <p className="text-gray-700">{detail.member.bio}</p>}
                <dl className="mt-3 space-y-2 text-sm">
                  <InfoRow label="Email" value={detail.member.email || 'Hidden by member'} />
                  <InfoRow
                    label="Phone"
                    value={detail.member.cell_phone || 'Hidden by member'}
                  />
                  <InfoRow label="Location" value={location || 'Not shared'} />
                  <InfoRow
                    label="Business"
                    value={detail.member.business_type || detail.member.industry || 'Not shared'}
                  />
                  <InfoRow label="Title" value={detail.member.title || 'Not shared'} />
                </dl>
              </InfoCard>
              {canViewPastoral && (
              <InfoCard title="Latest Snapshot">
                {detail.snapshots[0] ? (
                  <>
                    <p className="text-sm font-medium text-navy">
                      {detail.snapshots[0].month}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {detail.latest_snapshot_summary || 'No summary is available.'}
                    </p>
                  </>
                ) : (
                  <Empty>No finalized Snapshots yet.</Empty>
                )}
              </InfoCard>
              )}
              <InfoCard title="Attendance">
                {detail.attendance.length ? (
                  <ul className="space-y-2">
                    {detail.attendance.slice(0, 6).map((row) => (
                      <li key={row.month} className="flex justify-between text-sm">
                        <span>
                          {new Date(`${row.month}-01T12:00:00`).toLocaleDateString(
                            undefined,
                            { month: 'long', year: 'numeric' },
                          )}
                        </span>
                        <span
                          className={row.attended ? 'text-green-700' : 'text-gray-500'}
                        >
                          {row.attended ? 'Attended' : 'Absent'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Empty>No attendance has been recorded.</Empty>
                )}
              </InfoCard>
              {canViewPastoral && (
              <InfoCard title="Shared activity">
                <p className="text-sm text-gray-600">
                  {detail.grow_goals.length} shared grow goal
                  {detail.grow_goals.length === 1 ? '' : 's'} ·{' '}
                  {detail.shared_prayers.length} shared prayer
                  {detail.shared_prayers.length === 1 ? '' : 's'}
                </p>
              </InfoCard>
              )}
            </div>
          )}

          {currentTab === 'Billing' && (
            <div className="max-w-xl space-y-4">
              <div className="flex flex-wrap items-center gap-1.5">
                {detail.payment.is_paused && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    Paused
                  </span>
                )}
                {detail.payment.cancel_at_period_end && detail.payment.status !== 'canceled' && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-white"
                    title="Canceled in Stripe. They keep access until the period ends."
                  >
                    Leaving
                  </span>
                )}
                {detail.payment.limit_app_access && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Limited access
                  </span>
                )}
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  {BILLING_STATUS_LABEL[detail.payment.status]
                    ?? detail.payment.status.replace('_', ' ')}
                </span>
              </div>
              <dl className="space-y-2 text-sm text-gray-700">
                <div>
                  Card{' '}
                  <span className="font-medium text-navy">
                    {detail.payment.has_card ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  Last{' '}
                  <span className="font-medium text-navy">
                    {lastPaidLine(detail.payment.last_paid_at, detail.payment.last_paid_amount)}
                  </span>
                </div>
                <div>
                  {detail.payment.cancel_at_period_end && detail.payment.status !== 'canceled'
                    ? 'Access ends '
                    : 'Next '}
                  <span className="font-medium text-navy">
                    {nextChargeLine(detail.payment)}
                  </span>
                </div>
              </dl>
              <p className="text-sm text-gray-500">
                Change dues, skip, pause, or send an update-card email from Manage.
              </p>
              <button
                type="button"
                onClick={() => setManaging(true)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-navy hover:bg-gray-50"
              >
                <SlidersHorizontal size={14} /> Manage
              </button>
            </div>
          )}

          {currentTab === 'Snapshots' && canViewPastoral &&
            (detail.snapshots.length ? (
              <div className="space-y-4">
                {detail.snapshots.map((snapshot) => (
                  <article key={snapshot.id} className="border border-gray-100 rounded-xl p-4">
                    <h2 className="font-semibold text-navy">{snapshot.month}</h2>
                    {snapshot.summary && (
                      <p className="text-sm text-gray-600 mt-2">{snapshot.summary}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {snapshot.ratings.map((rating) => (
                        <span
                          key={rating.category}
                          className="text-xs px-2 py-1 bg-off-white text-gray-600 rounded-md"
                        >
                          {rating.category}: {rating.state_label || rating.score || '—'}
                          {rating.direction_label ? ` (${rating.direction_label})` : ''}
                        </span>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <Empty>No finalized Snapshots yet.</Empty>
            ))}

          {currentTab === 'Grow Goals' && canViewPastoral && (
            <div>
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={remindGrowGoal}
                  disabled={busyAction === 'reminder'}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  <Send size={12} />
                  {busyAction === 'reminder' ? 'Sending…' : 'Grow goal reminder'}
                </button>
              </div>
              {detail.grow_goals.length ? (
                <div className="space-y-3">
                  {detail.grow_goals.map((goal) => (
                    <article key={goal.id} className="border border-gray-100 rounded-xl p-4">
                      <p className="text-gray-800">{goal.description}</p>
                      <p className="text-xs text-gray-500 mt-2 capitalize">
                        {goal.status.replace('_', ' ')}
                        {goal.category ? ` · ${goal.category}` : ''}
                        {goal.due_date ? ` · Due ${formatDate(goal.due_date)}` : ''}
                      </p>
                    </article>
                  ))}
                </div>
              ) : (
                <Empty>No grow goals have been shared with facilitators.</Empty>
              )}
            </div>
          )}

          {currentTab === 'Prayers' && canViewPastoral &&
            (detail.shared_prayers.length ? (
              <div className="space-y-3">
                {detail.shared_prayers.map((prayer) => (
                  <article key={prayer.id} className="border border-gray-100 rounded-xl p-4">
                    <h2 className="font-medium text-navy">{prayer.title}</h2>
                    <p className="text-sm text-gray-600 mt-1">{prayer.content}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {prayer.is_answered ? 'Answered · ' : ''}
                      {formatDate(prayer.created_at)}
                    </p>
                  </article>
                ))}
              </div>
            ) : (
              <Empty>No non-private prayers have been shared.</Empty>
            ))}

          {currentTab === 'Facilitator Notes' && canViewPastoral && (
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                <Lock size={15} /> Only your own notes appear here.
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mb-5">
                <div className="relative flex-1">
                  <textarea
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    rows={3}
                    placeholder="Add a private facilitator note…"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-12 resize-none focus:ring-2 focus:ring-navy outline-none"
                  />
                  <MicButton
                    value={noteDraft}
                    onChange={setNoteDraft}
                    className="absolute bottom-2 right-2"
                  />
                </div>
                <button
                  onClick={addNote}
                  disabled={!noteDraft.trim() || busyAction === 'note'}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-navy text-white rounded-lg disabled:opacity-50 sm:self-start"
                >
                  <Plus size={16} /> Add note
                </button>
              </div>
              {detail.notes.length ? (
                <div className="space-y-3">
                  {detail.notes.map((note) => (
                    <article key={note.id} className="bg-off-white rounded-xl p-4">
                      {editingNote === note.id ? (
                        <>
                          <textarea
                            value={editDraft}
                            onChange={(event) => setEditDraft(event.target.value)}
                            rows={3}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 resize-none"
                          />
                          <div className="flex justify-end gap-3 mt-2 text-sm">
                            <button onClick={() => setEditingNote(null)}>Cancel</button>
                            <button
                              onClick={() => saveNote(note.id)}
                              className="font-medium text-navy"
                            >
                              Save
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {note.content}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              {formatDate(note.created_at)}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => {
                                setEditingNote(note.id);
                                setEditDraft(note.content);
                              }}
                              aria-label="Edit note"
                              className="p-2 text-gray-400 hover:text-navy"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => deleteNote(note.id)}
                              aria-label="Delete note"
                              className="p-2 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              ) : (
                <Empty>No private notes yet.</Empty>
              )}
            </div>
          )}

        </div>
      </div>
      {managing && (
        <ManageMemberById
          memberId={memberId}
          onClose={() => setManaging(false)}
          onSaved={refreshDetail}
        />
      )}
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-gray-100 rounded-xl p-4">
      <h2 className="font-semibold text-navy mb-3">{title}</h2>
      {children}
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="text-gray-700 capitalize">{value}</dd>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-500 py-4">{children}</p>;
}
